import dashboardService from 'ApiService/Admin/dashboardService';
import jobServices from 'ApiService/Placement/jobServices';
import Card from 'components/card';
import BarChart from 'components/charts/BarChart';
import InputField from 'components/fields/InputField';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import BatchPlacementChart from '../../components/BatchPlacementChart';

const getCurrentAcademicYear = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  return currentYear;
};

const generateAcademicYears = (currentYear, range = 5) => {
  const years = [];
  for (let i = -range; i <= range; i++) {
    years.push(currentYear + i);
  }
  return years;
};

const PlacementChart = () => {
  const currentAcademicYear = getCurrentAcademicYear();
  const years = generateAcademicYears(currentAcademicYear);

  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(currentAcademicYear);
  const [summaryData, setSummaryData] = useState({
    totalCourses: 0,
    totalStudents: 0,
    totalPlacedStudents: 0,
    overallPlacementPercentage: 0,
  });
  const [placementChartData, setPlacementChartData] = useState([]);
  const [barChartDataPlacements, setBarChartDataPlacements] = useState([]);
  const [xAxisCategories, setXAxisCategories] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [isCardVisible, setIsCardVisible] = useState(true); // Controls visibility of bar chart card
  const [isBatchChartVisible, setIsBatchChartVisible] = useState(false); // Controls visibility of BatchPlacementChart
  const [branch, setBranch] = useState([]);
  const [loadingBranch, setloadingBranch] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState('');

  // Date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterParams, setFilterParams] = useState({
    branch: '',
    startDate: '',
    endDate: '',
  });

  const { state } = useContext(AuthContext);

  const handleYearChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setSelectedYear(value);
  };

  const handleBranchChange = (event) => {
    const branchId = event.target.value;
    setSelectedBranch(branchId);
    // Update filterParams when branch changes
    setFilterParams((prev) => ({
      ...prev,
      branch: branchId,
    }));
  };

  const fetchBranch = async () => {
    setloadingBranch(true);
    try {
      const response = await jobServices.getBranch();
      if (response.data.success) {
        setBranch(response.data.allBranches || []);
      } else {
        toast.error('Failed to fetch Branches.');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch Branches.'
      );
    } finally {
      setloadingBranch(false);
    }
  };

  useEffect(() => {
    fetchBranch();
  }, []);

  const getData = async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getPlacementDataForChart(
        // selectedYear,
        filterParams.branch,
        filterParams.startDate,
        filterParams.endDate
      );

      if (response.data.success) {
        const { placementChartData, summary } = response.data;

        setPlacementChartData(placementChartData);
        setSummaryData({
          totalCourses: summary.totalCourses,
          totalStudents: summary.totalStudents,
          totalPlacedStudents: summary.totalPlacedStudents,
          overallPlacementPercentage: summary.overallPlacementPercentage,
        });

        const totalStudentsData = placementChartData.map(
          (data) => data.totalStudents
        );
        const placedStudentsData = placementChartData.map(
          (data) => data.placedStudents
        );
        const courseNames = placementChartData.map((data) => data.courseName);

        setBarChartDataPlacements([
          { name: 'Total Students', data: totalStudentsData },
          { name: 'Placed Students', data: placedStudentsData },
        ]);
        setXAxisCategories(courseNames);
      } else {
        toast.error('Failed to fetch placement data');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    getData();
  }, [filterParams]);

  const handleFilterClick = () => {
    setFilterParams({
      branch: selectedBranch,
      startDate,
      endDate,
    });
  };

  const handleResetClick = () => {
    setStartDate('');
    setEndDate('');

    setFilterParams({
      branch: selectedBranch,
      startDate: '',
      endDate: '',
    });
  };

  const handleBarClick = (dataPointIndex) => {
    const selectedCourseData = placementChartData.find(
      (data) => data.courseName === xAxisCategories[dataPointIndex]
    );

    if (selectedCourseData) {
      setSelectedCourse({
        courseId: selectedCourseData.courseId,
        courseName: selectedCourseData.courseName,
        startDate: filterParams.startDate,
        endDate: filterParams.endDate,
      });
      setIsCardVisible(false);
      setIsBatchChartVisible(true);
    }
  };

  const handleBatchChartClose = () => {
    setIsBatchChartVisible(false);
    setIsCardVisible(true);
    setSelectedCourse(null);
  };

  const barChartOptionsPlacements = {
    chart: {
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          handleBarClick(config.dataPointIndex);
        },
      },
    },
    tooltip: { theme: 'dark' },
    xaxis: {
      categories: xAxisCategories,
      labels: {
        show: true,
        style: {
          colors: '#A3AED0',
          fontSize: '12px',
          fontWeight: '500',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      show: false,
      labels: {
        show: true,
        style: {
          colors: '#CBD5E0',
          fontSize: '14px',
        },
      },
    },
    grid: {
      show: false,
      strokeDashArray: 5,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    fill: {
      type: 'gradient',
      gradient: {
        type: 'vertical',
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        colorStops: [
          [
            {
              offset: 0,
              color: 'var(--color-500)',
              opacity: 1,
            },
            {
              offset: 100,
              color: '#6F32E0',
              opacity: 0.0,
            },
          ],
          [
            {
              offset: 0,
              color: '#36A2EB',
              opacity: 1,
            },
            {
              offset: 100,
              color: 'rgba(255, 255, 255, 1)',
              opacity: 0.0,
            },
          ],
        ],
      },
    },
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        borderRadius: 5,
        columnWidth: '20px',
      },
    },
    legend: {
      markers: {
        fillColors: ['var(--color-500)', '#36A2EB'],
      },
      labels: {
        colors: 'dark:#CBD5E0',
      },
    },
  };

  return (
    <div>
      {isCardVisible && (
        <Card extra="pb-7 p-4" style={{ border: '3px solid var(--color-500)' }}>
          <div className="flex flex-col items-start justify-between lg:flex-row lg:items-center">
            <div className="mb-4 lg:mb-0 lg:mr-4">
              <p className="text-sm font-medium text-gray-600">
                Placement Overview
              </p>
              <div className="flex flex-wrap gap-4">
                <p className="text-l font-bold text-navy-700 dark:text-white">
                  Total Courses: {summaryData.totalCourses.toLocaleString()}
                </p>
                <p className="text-l font-bold text-green-600">
                  Total Students: {summaryData.totalStudents.toLocaleString()}
                </p>
                <p className="text-l font-bold text-blue-600">
                  Placed Students:{' '}
                  {summaryData.totalPlacedStudents.toLocaleString()}
                </p>
                <p className="text-l font-bold text-blue-600">
                  Remaining Students:{' '}
                  {(
                    summaryData.totalStudents - summaryData.totalPlacedStudents
                  ).toLocaleString()}
                </p>
                <p className="text-l font-bold text-purple-600">
                  Overall Placement:{' '}
                  {summaryData.overallPlacementPercentage.toFixed(2)}%
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-center md:gap-6">
              <div>
                <select
                  onChange={handleBranchChange}
                  value={selectedBranch}
                  name="branch"
                  className="mt-2 w-40 rounded-lg border border-gray-200 px-2 py-2 text-xs font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                >
                  <option value=""> Select Branch</option>
                  {loadingBranch ? (
                    <option>Loading Branch...</option>
                  ) : branch.length === 0 ? (
                    <option className="cursor-pointer font-bold text-brand-500">
                      No Branch available
                    </option>
                  ) : (
                    branch.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name} ({item.location})
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-end gap-4">
            <InputField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded border px-2 py-1"
            />
            <InputField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded border px-2 py-1"
            />
            <button
              onClick={handleFilterClick}
              className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
            >
              Filter
            </button>
            <button
              onClick={handleResetClick}
              className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
            >
              Reset
            </button>
          </div>

          <div className="h-[300px] w-full pb-0 pt-10">
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-lg">Loading...</div>
              </div>
            ) : placementChartData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-gray-500">
                No Data Found
              </div>
            ) : (
              <BarChart
                chartData={barChartDataPlacements}
                chartOptions={barChartOptionsPlacements}
              />
            )}
          </div>
        </Card>
      )}

      {isBatchChartVisible && selectedCourse && (
        <BatchPlacementChart
          selectedCourse={selectedCourse}
          onClose={handleBatchChartClose}
        />
      )}
    </div>
  );
};

export default PlacementChart;
